import React, { useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { MissionControlStatus, missionControlAddress } from "./mission-control.api";

export function MissionControlHelp({
  status,
  variant = "page",
}: {
  status?: MissionControlStatus;
  variant?: "page" | "dialog";
}): JSX.Element {
  const address = missionControlAddress(status);
  const certificate = status?.diagnostics?.certificate;
  const [copyFeedback, setCopyFeedback] = useState("");
  const revoked = certificate?.available && certificate.revocationStatus === "revoked";
  const expired = certificate?.available && !!certificate.notAfter && Date.parse(certificate.notAfter) < Date.now();
  const reportedAt = certificate?.reportedAt ? new Date(certificate.reportedAt * 1000).toLocaleString() : "unknown";
  const checkedAt = certificate?.checkedAt ? new Date(certificate.checkedAt * 1000).toLocaleString() : "unknown";
  const outdated =
    (!!certificate?.reportedAt && Date.now() / 1000 - certificate.reportedAt > 300) ||
    (!!certificate?.checkedAt && Date.now() / 1000 - certificate.checkedAt > 6 * 60 * 60);
  const actionStyle = variant === "dialog" ? { px: 0, minWidth: 0 } : undefined;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address!.href);
      setCopyFeedback("Address copied.");
    } catch {
      setCopyFeedback("Could not copy the address. Select and copy the URL above.");
    }
  };

  return (
    <Box
      component="section"
      aria-label="Mission Control connection details"
      sx={{ p: variant === "dialog" ? 0 : 2, mt: variant === "dialog" ? 2 : 0 }}
    >
      {(revoked || expired) && (
        <Alert severity={outdated ? "warning" : "error"} sx={{ mb: 1 }}>
          <Typography>
            {revoked
              ? "The device reported a revoked Mission Control certificate."
              : "The last reported Mission Control certificate has expired."}
            {" Contact Airgain support to refresh the certificate."}
          </Typography>
          <Typography variant="body2">
            {`Report received: ${reportedAt}. Checked: ${checkedAt}.`}
            {outdated && " This report may be outdated."}
          </Typography>
        </Alert>
      )}
      {address && (
        <>
          <Typography sx={{ overflowWrap: "anywhere" }}>Mission Control: {address.href}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            <Button component="a" href={address.href} target="_blank" rel="noopener noreferrer" sx={actionStyle}>
              Open in new tab
            </Button>
            <Button onClick={copyAddress} sx={actionStyle}>
              Copy URL
            </Button>
          </Stack>
          {copyFeedback && (
            <Typography role="status" variant="body2">
              {copyFeedback}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
